<template>
  <FaqAccordion class="content-faq" :groups="[{ items: questions }]" :multiple="false" />
</template>

<script setup lang="ts">
// Markdown `::faq` block (blog posts, help guides). Renders through the shared
// FaqAccordion, so every FAQ on the site looks and behaves the same.
interface FaqItem {
  q: string
  a: string
}

const props = defineProps<{
  items: FaqItem[]
}>()

const questions = computed(() => (props.items ?? []).map((item) => ({ question: item.q, answer: item.a })))

// One FAQPage block per page, however many `::faq` blocks the page has (the help
// FAQ has one per section): each block adds its questions to a per-route list.
// Google flags a page that carries two FAQPage blocks.
const route = useRoute()
const pageQuestions = useState<FaqItem[]>(`content-faq:${route.path}`, () => [])
for (const item of props.items ?? []) {
  if (!pageQuestions.value.some((existing) => existing.q === item.q)) pageQuestions.value.push(item)
}

useHead({
  script: computed(() =>
    pageQuestions.value.length
      ? [
          jsonLdScript('faq-jsonld', {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: pageQuestions.value.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          }),
        ]
      : []
  ),
})
</script>

<style scoped>
.content-faq {
  margin-block: 32px;
}
</style>
